import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { apiClient } from '../../framework/api/api.client';

interface ApiKeyManagementProps {
  tenantId: string;
  brandId?: string;
  locationId?: string;
  entityType: 'tenant' | 'brand' | 'location';
  entityName: string;
}

const apiKeyScopes = [
  { id: 'tenant-admin', name: 'Tenant Admin (Full Access)' },
  { id: 'brand-admin', name: 'Brand Admin' },
  { id: 'location-admin', name: 'Location Admin' },
  { id: 'sync-read', name: 'Sync Read (Pull data from parent)' },
  { id: 'sync-write', name: 'Sync Write (Push data to parent)' },
  { id: 'deployment', name: 'Application Deployment' },
  { id: 'monitoring', name: 'Device Monitoring' },
];

// Test JWT Section - Exchange API key for JWT token
const TestJwtSection: React.FC<{ apiKey: string }> = ({ apiKey }) => {
  const notify = useNotify();
  const [jwt, setJwt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showJwt, setShowJwt] = useState(false);
  const [jwtExpires, setJwtExpires] = useState<number | null>(null);
  
  const handleGenerateJwt = async () => {
    setLoading(true);
    try {
      const data = await apiClient.post('/auth/exchange', { apiKey });
      setJwt(data.access_token);
      setJwtExpires(data.expires_in);
      setShowJwt(true);
      notify('Test JWT generated successfully!', { type: 'success' });
    } catch (error: any) {
      console.error('Error generating test JWT:', error);
      notify(`Failed to generate JWT: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopyJwt = () => {
    if (jwt) {
      navigator.clipboard.writeText(jwt);
      notify('JWT copied to clipboard - paste into Swagger Authorization!', { type: 'success' });
    }
  };
  
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ color: '#4fc3f7' }}>
        🧪 Test JWT for Swagger/API Testing
      </Typography>
      
      {!jwt ? (
        <Button
          variant="outlined"
          color="primary"
          onClick={handleGenerateJwt}
          disabled={loading}
          fullWidth
        >
          {loading ? 'Generating...' : 'Generate Test JWT Token'}
        </Button>
      ) : (
        <Box>
          <Box sx={{ bgcolor: '#2d3748', p: 2, borderRadius: 1, border: '1px solid #4fc3f7', mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#4fc3f7' }}>
                JWT Token (expires in {jwtExpires}s):
              </Typography>
              <Box>
                <IconButton
                  size="small"
                  onClick={() => setShowJwt(!showJwt)}
                  sx={{ color: '#4fc3f7' }}
                >
                  {showJwt ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleCopyJwt}
                  sx={{ color: '#4fc3f7' }}
                  title="Copy JWT"
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'monospace', 
                color: '#e2e8f0',
                wordBreak: 'break-all',
                fontSize: '11px',
                maxHeight: '100px',
                overflow: 'auto'
              }}
            >
              {showJwt ? jwt : '••••••••••••••••••••••••••••••••••••••••••••••••••••'}
            </Typography>
          </Box>
          
          <Alert severity="success" icon={false}>
            <Typography variant="body2">
              ✅ <strong>JWT Ready!</strong> Copy the token and paste it into Swagger's "Authorize" dialog or use it directly in API requests.
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Header: <code>Authorization: Bearer {'<paste-jwt-here>'}</code>
            </Typography>
          </Alert>
          
          <Button
            variant="text"
            size="small"
            onClick={handleGenerateJwt}
            sx={{ mt: 1 }}
          >
            Refresh JWT
          </Button>
        </Box>
      )}
    </Box>
  );
};

export const ApiKeyManagement: React.FC<ApiKeyManagementProps> = ({
  tenantId,
  brandId,
  locationId,
  entityType,
  entityName,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  
  const [keys, setKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [newKeyData, setNewKeyData] = useState({
    name: '',
    description: '',
    scopes: ['sync-read'] as string[],
    expiresInDays: 365,
  });
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [showGeneratedKey, setShowGeneratedKey] = useState(false);
  const [testJwtDialog, setTestJwtDialog] = useState<{ open: boolean; keyId: string; keyName: string } | null>(null);
  const [openTestJwtDialog, setOpenTestJwtDialog] = useState(false);
  const [pastedApiKey, setPastedApiKey] = useState('');
  const [generatedTestJwt, setGeneratedTestJwt] = useState<string | null>(null);
  const [testJwtLoading, setTestJwtLoading] = useState(false);
  
  useEffect(() => {
    loadKeys();
  }, [tenantId, brandId, locationId]);
  
  const loadKeys = async () => {
    setLoading(true);
    try {
      const filter: any = { tenantId };
      if (locationId) filter.locationId = locationId;
      else if (brandId) filter.brandId = brandId;
      
      const { data } = await dataProvider.getList('api-key', {
        filter,
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'createdAt', order: 'DESC' }
      });
      
      setKeys(data);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateKey = async () => {
    try {
      const expiresAt = newKeyData.expiresInDays > 0
        ? new Date(Date.now() + newKeyData.expiresInDays * 24 * 60 * 60 * 1000)
        : undefined;
      
      // Call custom generate endpoint using apiClient
      const result = await apiClient.post('/api-key-dp/generate', {
        name: newKeyData.name,
        description: newKeyData.description,
        tenantId,
        brandId,
        locationId,
        scopes: newKeyData.scopes,
        expiresAt,
      });
      
      if (result.success) {
        setGeneratedKey(result.key);
        setShowGeneratedKey(true);
        notify('API key generated successfully!', { type: 'success' });
        loadKeys();
      } else {
        notify('Failed to generate API key', { type: 'error' });
      }
    } catch (error: any) {
      console.error('Error generating API key:', error);
      notify(`Error: ${error.message}`, { type: 'error' });
    }
  };
  
  const handleCopyKey = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey);
      notify('API key copied to clipboard', { type: 'success' });
    }
  };
  
  const handleCloseDialog = () => {
    setOpenGenerateDialog(false);
    setGeneratedKey(null);
    setShowGeneratedKey(false);
    setNewKeyData({
      name: '',
      description: '',
      scopes: ['sync-read'],
      expiresInDays: 365,
    });
  };
  
  const handleRevokeKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }
    
    try {
      await dataProvider.delete('api-key', {
        id: keyId,
        previousData: { id: keyId }
      });
      
      notify('API key revoked successfully', { type: 'success' });
      loadKeys();
    } catch (error: any) {
      console.error('Error revoking key:', error);
      notify(`Error: ${error.message}`, { type: 'error' });
    }
  };
  
  const handleScopeToggle = (scope: string) => {
    setNewKeyData(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope]
    }));
  };
  
  const handleGenerateTestJwt = async () => {
    if (!pastedApiKey) {
      notify('Please paste an API key', { type: 'warning' });
      return;
    }
    
    setTestJwtLoading(true);
    try {
      // Use raw fetch here - don't use apiClient since we don't want user auth headers
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5010';
      const response = await fetch(`${backendUrl}/auth/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: pastedApiKey })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setGeneratedTestJwt(data.access_token);
      notify('Test JWT generated successfully!', { type: 'success' });
    } catch (error: any) {
      console.error('Error generating test JWT:', error);
      notify(`Failed to generate JWT: ${error.message}`, { type: 'error' });
      setGeneratedTestJwt(null);
    } finally {
      setTestJwtLoading(false);
    }
  };
  
  const handleCloseTestJwtDialog = () => {
    setOpenTestJwtDialog(false);
    setPastedApiKey('');
    setGeneratedTestJwt(null);
  };
  
  const handleCopyTestJwt = () => {
    if (generatedTestJwt) {
      navigator.clipboard.writeText(generatedTestJwt);
      notify('JWT copied to clipboard - paste into Swagger!', { type: 'success' });
    }
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            API Keys for {entityName}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Generate API keys for instance-to-instance authentication and data synchronization
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<VpnKeyIcon />}
            onClick={() => setOpenTestJwtDialog(true)}
          >
            Generate Test JWT
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenGenerateDialog(true)}
          >
            Generate New Key
          </Button>
        </Box>
      </Box>
      
      {keys.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="textSecondary" align="center">
              No API keys generated yet. Create one to enable instance sync.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Key Prefix</TableCell>
                <TableCell>Scopes</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Used</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{key.name}</Typography>
                    {key.description && (
                      <Typography variant="caption" color="textSecondary">{key.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">{key.keyPrefix}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {key.scopes?.map((scope: string) => (
                        <Chip key={scope} label={scope} size="small" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={key.isActive ? 'Active' : 'Revoked'} 
                      color={key.isActive ? 'success' : 'default'} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString() : 'Never'}
                  </TableCell>
                  <TableCell>
                    {new Date(key.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    {key.isActive && (
                      <>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setTestJwtDialog({ open: true, keyId: key.id, keyName: key.name })}
                          title="Generate Test JWT"
                        >
                          <VpnKeyIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRevokeKey(key.id)}
                          title="Revoke Key"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Generate Key Dialog */}
      <Dialog open={openGenerateDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Generate New API Key</DialogTitle>
        <DialogContent>
          {!generatedKey ? (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Key Name"
                    value={newKeyData.name}
                    onChange={(e) => setNewKeyData({ ...newKeyData, name: e.target.value })}
                    fullWidth
                    required
                    helperText="e.g., 'Production Sync Key', 'Brand Manager Key'"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    value={newKeyData.description}
                    onChange={(e) => setNewKeyData({ ...newKeyData, description: e.target.value })}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Expires In (Days)"
                    type="number"
                    value={newKeyData.expiresInDays}
                    onChange={(e) => setNewKeyData({ ...newKeyData, expiresInDays: parseInt(e.target.value) })}
                    fullWidth
                    helperText="0 = never expires"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Permissions (Scopes):
                  </Typography>
                  <Grid container>
                    {apiKeyScopes.map((scope) => (
                      <Grid item xs={12} md={6} key={scope.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={newKeyData.scopes.includes(scope.id)}
                              onChange={() => handleScopeToggle(scope.id)}
                            />
                          }
                          label={scope.name}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  ✅ API Key Generated Successfully!
                </Typography>
                <Typography variant="body2">
                  Copy this key now - it will never be shown again for security reasons.
                </Typography>
              </Alert>
              
              <Box sx={{ bgcolor: '#1a202c', p: 2, borderRadius: 1, border: '1px solid #4fc3f7', mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: '#4fc3f7' }}>
                    Your API Key:
                  </Typography>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => setShowGeneratedKey(!showGeneratedKey)}
                      sx={{ color: '#4fc3f7' }}
                    >
                      {showGeneratedKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCopyKey}
                      sx={{ color: '#4fc3f7' }}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Box>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    color: '#e2e8f0',
                    wordBreak: 'break-all',
                    fontSize: '14px'
                  }}
                >
                  {showGeneratedKey ? generatedKey : '••••••••••••••••••••••••••••••••••••••••'}
                </Typography>
              </Box>
              
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Important:</strong> Store this key securely. You won't be able to retrieve it again.
                  If you lose it, you'll need to generate a new one.
                </Typography>
              </Alert>
              
              {/* Test JWT Generation */}
              <TestJwtSection apiKey={generatedKey} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!generatedKey ? (
            <>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button 
                onClick={handleGenerateKey} 
                variant="contained" 
                disabled={!newKeyData.name || newKeyData.scopes.length === 0}
              >
                Generate Key
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseDialog} variant="contained">
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Test JWT Dialog - Paste Existing Key */}
      <Dialog open={openTestJwtDialog} onClose={handleCloseTestJwtDialog} maxWidth="md" fullWidth>
        <DialogTitle>Generate Test JWT from API Key</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Paste an API key you have saved to exchange it for a JWT token for testing in Swagger.
              </Typography>
            </Alert>
            
            <TextField
              label="API Key"
              value={pastedApiKey}
              onChange={(e) => setPastedApiKey(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="mm_prod_abc123..."
              helperText="Paste the full API key here"
              sx={{ mb: 3 }}
            />
            
            {generatedTestJwt && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Generated JWT:
                </Typography>
                <Box sx={{ bgcolor: '#1a202c', p: 2, borderRadius: 1, border: '1px solid #4fc3f7', mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <IconButton
                      size="small"
                      onClick={handleCopyTestJwt}
                      sx={{ color: '#4fc3f7' }}
                      title="Copy JWT"
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      color: '#e2e8f0',
                      wordBreak: 'break-all',
                      fontSize: '11px',
                      maxHeight: '150px',
                      overflow: 'auto'
                    }}
                  >
                    {generatedTestJwt}
                  </Typography>
                </Box>
                <Alert severity="success">
                  <Typography variant="body2">
                    ✅ JWT generated! Copy and paste into Swagger's Authorize dialog.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTestJwtDialog}>Close</Button>
          <Button 
            onClick={handleGenerateTestJwt} 
            variant="contained"
            disabled={!pastedApiKey || testJwtLoading}
          >
            {testJwtLoading ? 'Generating...' : 'Generate JWT'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Connection Instructions */}
      {keys.length > 0 && (
        <Card sx={{ mt: 3, bgcolor: '#1a202c', color: '#e2e8f0', border: '1px solid #2d3748' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#4fc3f7' }}>
              Connection Instructions
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: '#cbd5e0' }}>
              Use these details to connect a child instance to this {entityType}:
            </Typography>
            
            <Box sx={{ bgcolor: '#2d3748', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '12px' }}>
              <Typography sx={{ color: '#e2e8f0', mb: 1 }}>
                <strong style={{ color: '#4fc3f7' }}>Parent Instance URL:</strong>
              </Typography>
              <Typography sx={{ color: '#e2e8f0', mb: 2 }}>
                {window.location.origin}
              </Typography>
              
              <Typography sx={{ color: '#e2e8f0', mb: 1 }}>
                <strong style={{ color: '#4fc3f7' }}>Authentication:</strong>
              </Typography>
              <Typography sx={{ color: '#e2e8f0', mb: 1 }}>
                Header: <code style={{ color: '#0f0' }}>Authorization: Bearer {'<your-api-key>'}</code>
              </Typography>
              
              <Typography sx={{ color: '#e2e8f0', mt: 2, mb: 1 }}>
                <strong style={{ color: '#4fc3f7' }}>Token Exchange:</strong>
              </Typography>
              <Typography sx={{ color: '#e2e8f0', mb: 2 }}>
                POST {window.location.origin}/auth/exchange
              </Typography>
              
              <Typography sx={{ color: '#e2e8f0', mb: 1 }}>
                <strong style={{ color: '#4fc3f7' }}>Sync Endpoint:</strong>
              </Typography>
              <Typography sx={{ color: '#e2e8f0' }}>
                GET {window.location.origin}/api/sync/available
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

